/**
 * Shared UI primitives for the auth screens. Intentionally small and
 * dependency-free (the shadcn/Radix component layer is built out in later sprints).
 *
 * `AuthCard` is the split-screen auth shell: a dark editorial brand panel on the
 * left (constant across every auth screen) and a light form panel on the right that
 * hosts the screen's heading + form. The form primitives below theme themselves off
 * the CSS custom properties the right panel declares (`--auth-*`), with dark
 * fallbacks so they degrade gracefully if ever used outside the shell.
 */
import { HIDE_PASSWORD_LABEL, SHOW_PASSWORD_LABEL } from '@argus/ui';
import { useId, useState, type CSSProperties, type JSX, type ReactNode } from 'react';

const SERIF =
  "'Iowan Old Style', 'Palatino Linotype', Palatino, 'Book Antiqua', Georgia, 'Times New Roman', serif";
/** Body text rides the app's `--font-sans` token (Manrope); the serif display face above is the shell's own. */
const SANS =
  "var(--font-sans, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif)";
const MONO = "var(--font-mono, ui-monospace, 'SF Mono', Menlo, Consolas, monospace)";

/**
 * Scoped styles for the auth shell. Injected once by the mounted AuthCard (only one
 * auth screen is ever mounted at a time). The `--auth-*` custom properties on
 * `.argus-auth__form-wrap` are the single theming surface the primitives read.
 */
const authStyles = `
html:has(.argus-auth), body:has(.argus-auth) { margin:0; padding:0; }
.argus-auth { min-height:100vh; display:grid; grid-template-columns:1.05fr 1fr; font-family:${SANS}; }
.argus-auth__brand {
  background:linear-gradient(160deg,#101a2e 0%,#0b1220 58%,#0a0f1b 100%);
  color:#e9eef7; padding:clamp(32px,4vw,56px); display:flex; flex-direction:column;
  justify-content:space-between; gap:48px;
}
.argus-auth__wordmark { margin:0; }
.argus-auth__wordmark img { display:block; width:min(200px, 48%); height:auto; }
.argus-auth__headline { font-family:${SERIF}; font-size:clamp(24px,2.4vw,31px); line-height:1.22; margin:0 0 16px; max-width:16ch; color:#f2f5fb; }
.argus-auth__blurb { margin:0; font-size:14px; line-height:1.6; color:rgba(233,238,247,.62); max-width:44ch; }
.argus-auth__form-col { background:#f4f2ec; display:flex; align-items:center; justify-content:center; padding:40px 24px; }
.argus-auth__form-wrap {
  width:100%; max-width:384px;
  --auth-field-bg:#fff; --auth-field-border:#dcd6c8; --auth-field-text:#1c2536;
  --auth-field-readonly-bg:#ece8dd;
  --auth-label:#6b6355; --auth-btn-bg:#1e2a44; --auth-btn-bg-hover:#27385c;
  --auth-btn-disabled:#c9c3b5; --auth-btn-text:#fdfdfb; --auth-heading:#1a2233;
  --auth-muted:#7a7365; --auth-link:#3a5a9a; --auth-danger:#b42318;
  --auth-success:#1f6b45; --auth-note-bg:#e9efe6; --auth-note-border:#c9d8c2;
  --auth-choice-hover-bg:#fbfaf6; --auth-choice-hover-border:#8a8272;
}
.argus-auth__heading { font-family:${SERIF}; font-size:30px; margin:0; color:var(--auth-heading); font-weight:600; letter-spacing:.01em; }
.argus-auth__subtitle { margin:6px 0 0; font-size:14px; line-height:1.5; color:var(--auth-muted); }
.argus-auth__body { margin-top:26px; }
.argus-auth input:focus-visible { outline:2px solid #1e2a44; outline-offset:1px; }
.argus-auth button:focus-visible, .argus-auth a:focus-visible { outline:2px solid #1e2a44; outline-offset:2px; }
.argus-auth button { transition:background-color .1s, color .1s, border-color .1s; }
.argus-auth button[type=submit]:not(:disabled):hover { background:var(--auth-btn-bg-hover); }
.argus-auth a { color:var(--auth-link); text-decoration:none; }
.argus-auth a:hover { text-decoration:underline; }
.argus-auth__secondary:not(:disabled):hover { background:var(--auth-choice-hover-bg); border-color:var(--auth-choice-hover-border); }
.argus-auth__choice:not(:disabled):hover { background:var(--auth-choice-hover-bg); border-color:var(--auth-choice-hover-border); }
.argus-auth__choice:disabled { opacity:.6; cursor:not-allowed; }
.argus-auth__codes { font-family:${MONO}; }
@media (max-width:860px) {
  .argus-auth { grid-template-columns:1fr; }
  .argus-auth__brand { padding:28px 24px; gap:0; }
  .argus-auth__headline, .argus-auth__blurb { display:none; }
  .argus-auth__form-col { padding:32px 20px; }
}
`;

export function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <main className="argus-auth">
      <style>{authStyles}</style>
      <aside className="argus-auth__brand">
        <div>
          {/* The full logo (glyph + wordmark + tagline), PO asset; the panel is always dark so
              the white-and-blue rendition is used. `/brand/argus-logo-light.png` exists for
              light surfaces. */}
          <h1 className="argus-auth__wordmark">
            <img src="/brand/argus-logo-dark.png" alt="ARGUS" width={623} height={647} />
          </h1>
        </div>
        <div>
          <p className="argus-auth__headline">
            Court-admissible forensic analysis, on a tamper-evident chain.
          </p>
          <p className="argus-auth__blurb">
            Statement parsing, excess-charge and tax computation grounded in Nigerian law — every
            figure sealed in a verifiable audit trail.
          </p>
        </div>
      </aside>
      <section className="argus-auth__form-col">
        <div className="argus-auth__form-wrap">
          <header>
            <h2 className="argus-auth__heading">{title}</h2>
            {subtitle ? <p className="argus-auth__subtitle">{subtitle}</p> : null}
          </header>
          <div className="argus-auth__body">{children}</div>
        </div>
      </section>
    </main>
  );
}

/** The auth-card field chrome (themed off `--auth-*`, dark fallbacks outside the shell). */
const authInputStyle: CSSProperties = {
  padding: '11px 12px',
  borderRadius: 8,
  border: '1px solid var(--auth-field-border, #24324a)',
  background: 'var(--auth-field-bg, #0f1930)',
  color: 'var(--auth-field-text, #e6edf6)',
  fontSize: 14,
};

/** The auth-card label: an uppercase micro-caption above the field. */
const authLabelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--auth-label, rgba(230,237,246,0.6))',
};

/** The in-app (Settings) field chrome — the tighter, sentence-case form those screens use. */
const compactInputStyle: CSSProperties = {
  padding: '10px 12px',
  borderRadius: 6,
  border: '1px solid var(--auth-field-border, #24324a)',
  background: 'var(--auth-field-bg, #0f1930)',
  color: 'var(--auth-field-text, #e6edf6)',
  fontSize: 14,
};

const compactLabelStyle: CSSProperties = { fontSize: 13 };

export function TextField({
  label,
  name,
  type = 'text',
  value,
  onChange,
  autoComplete,
  placeholder,
  inputMode,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  placeholder?: string;
  inputMode?: 'numeric' | 'text' | 'email';
  required?: boolean;
}): JSX.Element {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={authLabelStyle}>{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...(autoComplete ? { autoComplete } : {})}
        {...(placeholder ? { placeholder } : {})}
        {...(inputMode ? { inputMode } : {})}
        {...(required ? { required: true } : {})}
        style={authInputStyle}
      />
    </label>
  );
}

/** The auth-card chrome for a field that is shown but NOT editable. */
const readonlyInputStyle: CSSProperties = {
  ...authInputStyle,
  background: 'var(--auth-field-readonly-bg, #0b1426)',
  color: 'var(--auth-field-text, #e6edf6)',
  cursor: 'default',
};

/**
 * The account a password is being SET for — shown, and not editable.
 *
 * Why it exists: a password manager pairs a saved credential with the `autocomplete="username"`
 * field in the SAME form. A screen that CREATES a password without one gives the manager nothing
 * to attach the new password to, so it may never offer to save it. That is exactly what the PO
 * hit accepting a real invitation on staging: a password set on `/invite/<token>` that no manager
 * captured.
 *
 *  - VISIBLE + `readOnly`, deliberately NOT `type="hidden"` — several managers ignore hidden
 *    fields, and showing the address is the honest answer to "which account is this for?";
 *  - `readOnly`, never `disabled`: a disabled control is skipped by managers too;
 *  - non-editable by construction, and the value is never read back out of the DOM — the request
 *    is authorised by the token the screen was opened with, so this can never become a way to
 *    act as a different address.
 */
export function ReadonlyIdentityField({
  label,
  name,
  value,
}: {
  label: string;
  name: string;
  value: string;
}): JSX.Element {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={authLabelStyle}>{label}</span>
      <input
        type="email"
        name={name}
        value={value}
        readOnly
        autoComplete="username"
        style={readonlyInputStyle}
      />
    </label>
  );
}

/** The toggle's accessible name in each state (asserted by the tests; never re-worded in place). */
// The reveal toggle's labels are the shared `Input reveal` ones (@argus/ui field.tsx), re-exported
// here so the auth screens and their tests keep this import path.
export { HIDE_PASSWORD_LABEL, SHOW_PASSWORD_LABEL };

/**
 * Focus/hover chrome for the reveal toggle. Inline styles cannot express `:focus-visible`,
 * and the shell's own `.argus-auth input:focus-visible` rule does not reach Settings — so the
 * field carries its own (idempotent) rule wherever it is mounted.
 */
const revealStyles = `
.argus-reveal { transition:color .12s ease; }
.argus-reveal:hover { color:var(--auth-field-text, #e6edf6); }
.argus-reveal:focus-visible { outline:2px solid var(--auth-btn-bg, #8fb3ff); outline-offset:2px; }
`;

/** The eye / eye-with-slash glyph. Decorative: the accessible name lives on the button. */
function EyeIcon({ crossed }: { crossed: boolean }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.8 12S5.9 5.2 12 5.2 22.2 12 22.2 12 18.1 18.8 12 18.8 1.8 12 1.8 12Z" />
      <circle cx="12" cy="12" r="3.1" />
      {crossed ? <path d="M3.2 3.2 20.8 20.8" /> : null}
    </svg>
  );
}

/**
 * A password input with a reveal toggle — the ONE control every password field in the app
 * uses (the PO's "the password field doesn't have an eye", reported from the invitation
 * flow, where a silent typo in a password you cannot read costs an account recovery).
 *
 * Accessibility
 *  - the toggle is a real `<button type="button">`, so it can never submit the form it sits in;
 *  - its accessible name flips between "Show password" and "Hide password", and `aria-pressed`
 *    reports the state to assistive tech;
 *  - it is in the natural tab order (a native button, no `tabIndex` override) and carries a
 *    visible `:focus-visible` ring;
 *  - the input keeps an explicit `<label for>` association (the button is deliberately OUTSIDE
 *    the label, so it never leaks into the field's accessible name) and passes `aria-describedby`
 *    straight through for error wiring.
 *
 * Security
 *  - `revealed` is per-field component state, defaulting to hidden. It is never lifted, stored
 *    or logged, so unmounting the screen (any navigation) resets it — a revealed password can
 *    never survive into another screen.
 *  - It deliberately does NOT re-hide on submit: a successful submit navigates away (the unmount
 *    resets it anyway), while a REFUSED submit is the exact moment the user needs to read what
 *    they typed. A submit is not a change of audience; navigation is, and that is covered.
 *  - `autocomplete` and `name` are passed through untouched so password managers still recognise
 *    (and offer to fill/save) the field.
 */
export function PasswordField({
  label,
  name,
  value,
  onChange,
  autoComplete,
  placeholder,
  required,
  describedBy,
  variant = 'auth',
}: {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  /** Explicit by design: a new password must never claim `current-password` (and vice versa). */
  autoComplete: 'current-password' | 'new-password';
  placeholder?: string;
  required?: boolean;
  /** Id of the element describing this field (an error message), wired as `aria-describedby`. */
  describedBy?: string;
  /** `auth` — the auth-card chrome; `compact` — the in-app (Settings) form chrome. */
  variant?: 'auth' | 'compact';
}): JSX.Element {
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  const compact = variant === 'compact';

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <style>{revealStyles}</style>
      <label htmlFor={id} style={compact ? compactLabelStyle : authLabelStyle}>
        {label}
      </label>
      <div style={{ position: 'relative', display: 'grid' }}>
        <input
          id={id}
          type={revealed ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          {...(placeholder ? { placeholder } : {})}
          {...(required ? { required: true } : {})}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          style={{ ...(compact ? compactInputStyle : authInputStyle), paddingRight: 44 }}
        />
        <button
          type="button"
          className="argus-reveal"
          onClick={() => setRevealed((r) => !r)}
          aria-pressed={revealed}
          aria-label={revealed ? HIDE_PASSWORD_LABEL : SHOW_PASSWORD_LABEL}
          aria-controls={id}
          style={{
            position: 'absolute',
            top: '50%',
            right: 4,
            transform: 'translateY(-50%)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            padding: 0,
            border: 0,
            borderRadius: 6,
            background: 'transparent',
            color: 'var(--auth-label, rgba(230,237,246,0.6))',
            cursor: 'pointer',
          }}
        >
          <EyeIcon crossed={revealed} />
        </button>
      </div>
    </div>
  );
}

/** The primary auth-card button. A form submit by default; `type="button"` + `onClick` for an action. */
export function SubmitButton({
  children,
  disabled,
  style,
  type = 'submit',
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  style?: CSSProperties;
  type?: 'submit' | 'button';
  onClick?: () => void;
}): JSX.Element {
  return (
    <button
      type={type}
      disabled={disabled === true}
      {...(onClick ? { onClick } : {})}
      style={{
        width: '100%',
        padding: '12px 14px',
        borderRadius: 8,
        border: 0,
        background:
          disabled === true ? 'var(--auth-btn-disabled, #24324a)' : 'var(--auth-btn-bg, #2f6feb)',
        color: 'var(--auth-btn-text, #ffffff)',
        // design-system.md §14: a disabled control says so with the cursor, not just its colour.
        cursor: disabled === true ? 'not-allowed' : 'pointer',
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: '0.01em',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/** The auth-card secondary action (outlined, themed like a field): never a form submit. */
export function SecondaryButton({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="argus-auth__secondary"
      disabled={disabled === true}
      onClick={onClick}
      style={{
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid var(--auth-field-border, #24324a)',
        background: 'var(--auth-field-bg, #0f1930)',
        color: 'var(--auth-field-text, #e6edf6)',
        cursor: disabled === true ? 'not-allowed' : 'pointer',
        fontSize: 14,
        fontWeight: 600,
      }}
    >
      {children}
    </button>
  );
}

/**
 * A checkbox with its label text beside it. The input and the text are separate flex items
 * with a fixed gap, so the text can never run on straight from the box (the enrolment
 * screen's "I have saved…" line did exactly that when the label's text node sat flush
 * against the `<input>`).
 */
export function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}): JSX.Element {
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        fontSize: 13,
        lineHeight: 1.45,
        color: 'var(--auth-field-text, #e6edf6)',
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ margin: '2px 0 0', flex: 'none', width: 16, height: 16, cursor: 'pointer' }}
      />
      <span>{label}</span>
    </label>
  );
}

/**
 * Non-revealing error text (CC-7) — shows the typed ApiError message, never a stack.
 *
 * `role="alert"` announces it when it appears; pass `id` and point the field it is about at that
 * id with `PasswordField`'s `describedBy` so the error is also programmatically ASSOCIATED with
 * the field (a user who tabs back to it hears why it was refused, not just once on render).
 * Renders nothing without a message, so a caller must only wire `describedBy` while one is shown.
 */
export function FormError({
  message,
  id,
  requestId,
}: {
  message: string | null;
  id?: string;
  /** F-37: the refused request's correlation id, shown as "Reference: <id>" for support. */
  requestId?: string | null | undefined;
}): JSX.Element | null {
  if (!message) return null;
  return (
    <p
      {...(id ? { id } : {})}
      role="alert"
      style={{ margin: 0, color: 'var(--auth-danger, #f97066)', fontSize: 13 }}
    >
      {message}
      {requestId ? (
        <span
          style={{
            display: 'block',
            marginTop: 4,
            fontSize: 11,
            opacity: 0.8,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            wordBreak: 'break-all',
          }}
        >
          Reference: <code style={{ font: 'inherit', userSelect: 'all' }}>{requestId}</code>
        </span>
      ) : null}
    </p>
  );
}

/**
 * A non-error notice (a completed action, a confirmation) — `role="status"`, so it is announced
 * politely rather than as an alert. Renders nothing without a message.
 */
export function FormStatus({ message }: { message: string | null }): JSX.Element | null {
  if (!message) return null;
  return (
    <p
      role="status"
      style={{
        margin: 0,
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid var(--auth-note-border, #24324a)',
        background: 'var(--auth-note-bg, #0f1930)',
        color: 'var(--auth-success, #8fd3ad)',
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      {message}
    </p>
  );
}
